import {
  SchemaDefinition,
  SchemaTransformError,
  UnknownSchema
} from '@rehaje/core'

import type { MappingFunctionSpecific } from './TypedNode'
import type { TypedSchema } from '@rehaje/core'

export interface SchemaMappingFunctionSpecific extends RehajeSchemaMetadata {
  default?: Function | MappingFunctionSpecific
  item?: TypedSchema
  return?: TypedSchema
  type: 'Schema.MappingFunction'
  value?: any
}

export class SchemaMappingFunctionDefinition extends SchemaDefinition {
  override defaultsMerge(
    value: any,
    schema: SchemaMappingFunctionSpecific,
    initialize: boolean
  ) {
    if (value === undefined && initialize) {
      value = schema.default
    } else if (value === undefined && schema.required) {
      value = schema.default ?? { type: 'MappingFunction' }
    }

    if (typeof value === 'object' && value !== null) {
      value.type ??= 'MappingFunction'
    }
    if (typeof value?.type === 'string' && schema.return !== undefined) {
      value.return = super.defaultsMerge(
        value.return,
        schema.return,
        initialize
      )
    }

    return value
  }

  override getIn(
    schema: SchemaMappingFunctionSpecific,
    path: PropertyKey[]
  ): TypedSchema {
    if (path.length === 0) return schema
    if (path[0] === 'return' && schema.return) {
      return super.getIn(schema.return, path.slice(1))
    }
    return UnknownSchema
  }

  override infer(
    target: any,
    schema: SchemaMappingFunctionSpecific,
    path: PropertyKey[] = []
  ): SchemaMappingFunctionSpecific {
    return {
      ...schema,
      ...this.inferOnlySchema(schema, path),
      value: target
    }
  }

  override inferOnlySchema(
    schema: SchemaMappingFunctionSpecific,
    path: PropertyKey[] = []
  ) {
    return {
      ...schema,
      item: schema.item
        ? super.inferOnlySchema(schema.item, path.concat('item'))
        : undefined,
      return: schema.return
        ? super.inferOnlySchema(schema.return, path.concat('return'))
        : undefined
    }
  }

  override inferTypeScript(schema: SchemaMappingFunctionSpecific): string {
    if (typeof schema.value === 'function') return 'Function'
    return `${this.interface}<${schema.return ? super.inferTypeScript(schema.return, []) : 'undefined'}>`
  }

  override prune(target: any): any {
    return target
  }

  override transform(
    target: any,
    schema: SchemaMappingFunctionSpecific,
    path: PropertyKey[] = []
  ) {
    if (this.test(target, schema, path)) return target
    if (target !== schema.fallback)
      this.raise(new SchemaTransformError(schema.name))
    return target
  }

  override validate(target: any, schema: SchemaMappingFunctionSpecific) {
    this.validateMetadata(target, schema, (target) => {
      return typeof target === 'function' || this.isRelationSpecific(target)
    })
  }
}

declare global {
  export interface RehajeSpecifics {
    'Schema.MappingFunction': SchemaMappingFunctionSpecific
  }
}
